import { useTranslation } from 'react-i18next';

import BorderStyleDashed from '@/assets/icons/cg_border_style_dashed.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';
import { OverviewValueWithTrendTooltip } from '@/components/molecules/OverviewValueWithTrendTooltip';
import { alertIconColors } from '@/config/settings';
import { speciesTraits } from '@/domain/cages/speciesTraits';
import type { OverviewCage } from '@/domain/client';
import {
    shouldShowBetaWoundOverviewCell,
    shouldShowLegacyWoundOverviewCell,
} from '@/routes/Client/Cage/pages/Health/_utils/overviewHealthColumnVisibility';
import { createCageUrl } from '@/utils/urlGenerator';

import { HealthOverviewCellLink } from './HealthOverviewCellLink';

export interface LocationOverviewWoundsCellProps {
    cage: OverviewCage;
    clientId: number;
    locationId: number;
    isHealthModuleEnabled: boolean;
}

export default function LocationOverviewWoundsCell(props: LocationOverviewWoundsCellProps) {
    const { cage, clientId, locationId, isHealthModuleEnabled } = props;
    const { t } = useTranslation();
    const bioscopeVersion = cage.bioscopeVersion;

    // The column is up when ANY pen at the location has wound scoring, so a pen without it
    // sitting beside one must not render a wound rate. Read as a trait rather than compared to
    // a species, so a species added later keeps the default instead of silently losing wounds.
    if (!speciesTraits(cage.fishType?.shortName).woundScoring) {
        return <span>---</span>;
    }

    if (shouldShowLegacyWoundOverviewCell(isHealthModuleEnabled, bioscopeVersion)) {
        const welfare = cage.cageData.welfare;

        return (
            <OverviewValueWithTrendTooltip
                value={welfare?.woundRate}
                growth={welfare?.woundSlope}
                showPercentage
                to={`${createCageUrl(clientId, locationId, cage.cageId)}/welfare/wounds`}
            />
        );
    }

    if (shouldShowBetaWoundOverviewCell(isHealthModuleEnabled, bioscopeVersion)) {
        const health = cage.cageData.health;
        const linkTo = `${createCageUrl(
            clientId,
            locationId,
            cage.cageId
        )}/health/body-wounds/insight`;
        const woundRate = health?.woundRate;

        if (health == null || woundRate == null || Number.isNaN(woundRate)) {
            return (
                <Tooltip content={t('common.labels.noDataFound')} layout="fit">
                    <HealthOverviewCellLink
                        to={linkTo}
                        ariaLabel={t('common.labels.noDataFound')}
                        state="no-data">
                        <SvgIcon
                            icon={BorderStyleDashed}
                            size="custom"
                            customSize="1.7em"
                            color={alertIconColors.neutral}
                        />
                    </HealthOverviewCellLink>
                </Tooltip>
            );
        }

        return (
            <OverviewValueWithTrendTooltip
                value={woundRate}
                growth={health.woundSlope}
                showPercentage
                to={linkTo}
            />
        );
    }

    return <span>---</span>;
}
